import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { dirPickerSupported, getHandles, getAccessStates, requestAccessAll, type TargetKind } from '@/lib/fileTargets';

const KINDS: TargetKind[] = ['scan', 'signed', 'nfc'];

/**
 * Welle 9 (pre89f): Globale Benachrichtigung, sobald gemerkte Zielordner nach Sitzungsstart
 * (oder nach entzogener Freigabe) neu bestätigt werden müssen. „Jetzt bestätigen" fragt die
 * Berechtigung für ALLE Ordner mit einem Klick neu an (synchron → Nutzer-Geste bleibt erhalten).
 * Prüft bei Mount und immer, wenn der Tab wieder den Fokus bekommt.
 */
export function FolderAccessBanner() {
  const supported = dirPickerSupported();
  const handlesRef = useRef<Record<TargetKind, any | null>>({ scan: null, signed: null, nfc: null });
  const [count, setCount] = useState(0);
  const [need, setNeed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  async function check() {
    try {
      const handles = await getHandles();
      handlesRef.current = handles;
      const st = await getAccessStates(handles);
      const pending = KINDS.filter((k) => handles[k] && st[k] !== 'granted');
      setCount(pending.length);
      setNeed(pending.length > 0);
      if (pending.length === 0) setDismissed(false); // bei künftigem Bedarf wieder anzeigen
    } catch { /* */ }
  }

  useEffect(() => {
    if (!supported) return;
    void check();
    const onFocus = () => { void check(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported]);

  function confirm() {
    // requestAccessAll feuert die requestPermission-Aufrufe SYNCHRON (kein await davor!).
    requestAccessAll(handlesRef.current)
      .then(() => getAccessStates(handlesRef.current))
      .then((st) => {
        const handles = handlesRef.current;
        const pending = KINDS.filter((k) => handles[k] && st[k] !== 'granted');
        setCount(pending.length);
        setNeed(pending.length > 0);
      })
      .catch(() => { /* */ });
  }

  if (!supported || !need || dismissed) return null;
  return (
    <div style={bar} role="alert">
      <span style={{ fontSize: 18, flexShrink: 0 }}>🔓</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.4 }}>
        <strong>Ordnerzugriff bestätigen.</strong> Für die automatische Ablage der Dokumente muss der Zugriff
        auf {count === 1 ? 'den gemerkten Ordner' : `die ${count} gemerkten Ordner`} für diese Sitzung neu bestätigt werden.
      </span>
      <button onClick={confirm} style={primary}>Jetzt bestätigen</button>
      <button onClick={() => setDismissed(true)} style={closeBtn} title="Ausblenden">✕</button>
    </div>
  );
}

const bar: CSSProperties = {
  position: 'fixed', top: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 1200,
  display: 'flex', alignItems: 'center', gap: 12, maxWidth: 'min(700px, 94vw)',
  padding: '10px 12px 10px 14px', background: 'var(--bg-elev-2)',
  border: '1px solid var(--tenant-accent-soft)', borderLeft: '3px solid var(--tenant-accent)',
  borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
};
const primary: CSSProperties = {
  border: 'none', background: 'var(--tenant-accent)', color: '#fff', borderRadius: 7,
  padding: '7px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
};
const closeBtn: CSSProperties = {
  border: 'none', background: 'transparent', color: 'var(--muted)', fontSize: 14, cursor: 'pointer', flexShrink: 0, lineHeight: 1,
};
